import type { ReactNode } from "react"
import type { ConfigField, Registry } from "@/api.js"
import { Badge } from "@/components/ui/badge.js"
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card.js"
import { Input, Label } from "@/components/ui/input.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { Slider } from "@/components/ui/slider.js"
import { Switch } from "@/components/ui/switch.js"
import { cn } from "@/lib/utils.js"
import { Conditions } from "./Conditions.js"
import { vocabularyOf } from "./condition-text.js"
import { FieldControl } from "./Field.js"
import { type EditorRule, laneOf, laneTitle } from "./model.js"

/** Short on the control; the registry's own option label says the rest underneath. */
const MODES: readonly { value: EditorRule["mode"]; label: string }[] = [
  { value: "veto", label: "Weto" },
  { value: "score", label: "Punkty" },
]

/**
 * One rule, the whole of it, with room.
 *
 * **Everything that sets a rule is here, laid out as a form: a label, the
 * control, and under it the sentence that says what the control does.** The
 * sentences are the registry's — `commonRuleFields` for what every rule has,
 * the manifest's own fields for what only this rule has — so the panel carries
 * no copy of its own about what a veto is, and a setting added to every rule
 * arrives here with its explanation attached. The grid this replaces had the
 * same controls at cell size with the explanation one hover away; here a
 * person reads *why* before they drag.
 *
 * The description comes first, because it is the argument for the weight
 * somebody is about to set, and the codes the rule writes come with it: they
 * are what a ticket quotes, so "which rule wrote `gus.founded_recently`" is
 * answered on the screen where the rule is set.
 */
export function RuleEditor({
  rule,
  registry,
  threshold,
  priority,
  onChange,
}: {
  rule: EditorRule
  registry: Registry
  threshold: number
  /** Where it runs, or null for a rule that does not. */
  priority: number | null
  onChange: (next: EditorRule) => void
}) {
  const lane = laneTitle(laneOf(rule, registry)).toLowerCase()

  return (
    <Card>
      <CardHeader className="gap-1.5 px-5 py-4">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <CardTitle className="text-[17px] tracking-tight">{rule.title}</CardTitle>
          {!rule.enabled && (
            <Badge tone="neutral" glyph="○">
              wyłączona
            </Badge>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
          <span className="font-mono">{rule.ruleId}</span>
          {rule.dependsOn.map((providerId) => (
            <Badge key={providerId} tone="neutral" className="font-mono text-[11.5px]">
              {providerId}
            </Badge>
          ))}
          <span className="tabular-nums">
            {priority === null ? "nie jest pytana" : `#${priority} · ${lane}`}
          </span>
        </div>
      </CardHeader>

      <CardBody className="space-y-7 px-5 py-5">
        <section className="space-y-3">
          <p className="max-w-[64ch] text-[13.5px] leading-6 text-foreground">{rule.description}</p>
          {rule.reasonCodes.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-[12.5px] text-muted-foreground">Kody w audycie</span>
              {rule.reasonCodes.map((code) => (
                <Badge key={code} tone="neutral" className="font-mono text-[11.5px]">
                  {code}
                </Badge>
              ))}
            </div>
          )}
        </section>

        <section>
          <SectionTitle>Ustawienia</SectionTitle>
          <CommonSettings
            rule={rule}
            registry={registry}
            threshold={threshold}
            onChange={onChange}
          />
        </section>

        {/*
         * **When the rule runs at all, beside everything else that sets it.**
         * It decides whether the rule is asked, which for a metered source is
         * whether money is spent, so it belongs in the form rather than behind
         * a disclosure — and the builder is rendered from the vocabulary the
         * registry serves, so a field added to `context` for a second question
         * arrives here without an edit.
         */}
        <section>
          <Conditions
            conditions={rule.conditions}
            vocabulary={vocabularyOf(registry)}
            onChange={(conditions) => onChange({ ...rule, conditions })}
          />
        </section>

        {rule.fields.length > 0 && (
          <section>
            <SectionTitle>Parametry</SectionTitle>
            {/*
             * Rendered from what the rule declared, never from a form written
             * per integration — the only part of this editor whose shape
             * belongs to the rule rather than to every rule.
             */}
            <div className="divide-y">
              {rule.fields.map((field) => (
                <ParameterRow
                  key={field.key}
                  rule={rule}
                  field={field}
                  onChange={(next) =>
                    onChange({ ...rule, params: { ...rule.params, [field.key]: next } })
                  }
                />
              ))}
            </div>
          </section>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * What every rule has, each row named and explained by the registry's
 * `commonRuleFields` — the one place that copy lives, so a setting added to
 * every rule arrives here with its label, its options and its help attached.
 */
function CommonSettings({
  rule,
  registry,
  threshold,
  onChange,
}: {
  rule: EditorRule
  registry: Registry
  threshold: number
  onChange: (next: EditorRule) => void
}) {
  const enabled = commonField(registry, "enabled", "Włączona")
  const mode = commonField(registry, "mode", "Tryb")
  const weight = commonField(registry, "weight", "Waga")
  const unavailable = commonField(registry, "onUnavailable", "Gdy provider nie odpowie")

  return (
    <div className="divide-y">
      <SettingRow id={`${rule.ruleId}-enabled`} label={enabled.label} help={enabled.help}>
        <div className="flex h-8 items-center gap-2.5">
          <Switch
            id={`${rule.ruleId}-enabled`}
            checked={rule.enabled}
            aria-label={enabled.label}
            onCheckedChange={(next) => onChange({ ...rule, enabled: next })}
          />
          <span className="text-[13px] text-muted-foreground">{rule.enabled ? "Tak" : "Nie"}</span>
        </div>
      </SettingRow>

      <SettingRow
        label={mode.label}
        help={mode.options.find((option) => option.value === rule.mode)?.label}
      >
        <ModeSwitch
          name={`${rule.ruleId}-mode`}
          label={mode.label}
          value={rule.mode}
          onChange={(next) => onChange({ ...rule, mode: next })}
        />
      </SettingRow>

      <SettingRow
        id={`${rule.ruleId}-weight`}
        label={weight.label}
        help={weightHelp(rule, threshold, weight.help)}
      >
        <Weight rule={rule} threshold={threshold} onChange={onChange} />
      </SettingRow>

      <SettingRow
        id={`${rule.ruleId}-unavailable`}
        label={unavailable.label}
        help={unavailable.help}
      >
        <Select
          value={rule.onUnavailable}
          onValueChange={(value) =>
            onChange({ ...rule, onUnavailable: value as EditorRule["onUnavailable"] })
          }
        >
          <SelectTrigger
            id={`${rule.ruleId}-unavailable`}
            className="w-full max-w-md"
            aria-label={unavailable.label}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {unavailable.options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingRow>
    </div>
  )
}

interface CommonField {
  readonly label: string
  readonly help?: string | undefined
  readonly options: readonly { readonly value: string; readonly label: string }[]
}

/** One of `commonRuleFields`, with a label to fall back on if a registry lacks it. */
function commonField(registry: Registry, key: string, fallbackLabel: string): CommonField {
  const field = registry.commonRuleFields.find((candidate) => candidate.key === key)
  return { label: field?.label ?? fallbackLabel, help: field?.help, options: field?.options ?? [] }
}

/** The weight row's sentence depends on what the weight currently means. */
function weightHelp(
  rule: EditorRule,
  threshold: number,
  help: string | undefined,
): string | undefined {
  if (rule.mode === "veto") return "Weto nie ma wagi — blokuje samo."
  if (rule.weight >= threshold) return "Ta waga sięga progu: reguła blokuje sama, jak weto."
  return help
}

function SectionTitle({ children }: { children: ReactNode }) {
  return <h4 className="mb-1 text-[13px] font-semibold text-foreground">{children}</h4>
}

/**
 * One setting: its name on the left, the control on the right, and the sentence
 * that explains the control under it. The same row for a setting every rule
 * has and for one only this rule declares, so the form reads as one form.
 */
function SettingRow({
  id,
  label,
  help,
  children,
}: {
  id?: string
  label: string
  help?: string | undefined
  children: ReactNode
}) {
  return (
    <div className="grid gap-x-6 gap-y-1.5 py-3.5 sm:grid-cols-[minmax(0,11rem)_minmax(0,1fr)]">
      <Label htmlFor={id} className="pt-1.5 leading-5">
        {label}
      </Label>
      <div className="min-w-0 space-y-1.5">
        {children}
        {help !== undefined && (
          <p className="max-w-prose text-[12.5px] leading-5 text-muted-foreground">{help}</p>
        )}
      </div>
    </div>
  )
}

function ParameterRow({
  rule,
  field,
  onChange,
}: {
  rule: EditorRule
  field: ConfigField
  onChange: (next: string | number | boolean) => void
}) {
  const id = `${rule.ruleId}-${field.key}`
  return (
    <SettingRow id={id} label={field.label} help={field.help}>
      <FieldControl id={id} field={field} value={rule.params[field.key]} onChange={onChange} />
    </SettingRow>
  )
}

/**
 * Veto or points, both offered on every rule, side by side rather than in a
 * menu: with two options the choice is the control. Real radio inputs under
 * the styling, so the keyboard and a screen reader get a radio group for free;
 * a veto wears the risk colour where it is chosen, so a hard exclusion is
 * visible from across the screen.
 */
function ModeSwitch({
  name,
  label,
  value,
  onChange,
}: {
  name: string
  label: string
  value: EditorRule["mode"]
  onChange: (next: EditorRule["mode"]) => void
}) {
  return (
    <fieldset className="inline-flex h-8 items-center rounded-md border border-input bg-muted p-0.5">
      <legend className="sr-only">{label}</legend>
      {MODES.map((mode) => {
        const chosen = value === mode.value
        return (
          <label
            key={mode.value}
            className={cn(
              "flex h-full cursor-pointer items-center rounded px-3 text-[13px] transition-colors has-focus-visible:ring-2 has-focus-visible:ring-ring",
              chosen
                ? cn(
                    "bg-card font-medium shadow-xs",
                    mode.value === "veto" ? "text-critical" : "text-foreground",
                  )
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <input
              type="radio"
              name={name}
              value={mode.value}
              checked={chosen}
              className="sr-only"
              onChange={() => onChange(mode.value)}
            />
            {mode.label}
          </label>
        )
      })}
    </fieldset>
  )
}

/**
 * The weight, and how far it is from deciding on its own.
 *
 * **The slider's maximum is the block threshold**, so a slider at the end means
 * "this rule alone blocks" and the number stops being abstract — that is the
 * one thing about a weight a bare number cannot say. The box beside it is for
 * the times somebody wants an exact figure rather than a gesture.
 */
function Weight({
  rule,
  threshold,
  onChange,
}: {
  rule: EditorRule
  threshold: number
  onChange: (next: EditorRule) => void
}) {
  if (rule.mode === "veto") {
    return <div className="flex h-8 items-center text-[13px] text-muted-foreground">—</div>
  }

  const decisive = rule.weight >= threshold

  return (
    <div className="flex h-8 max-w-md items-center gap-3">
      <Input
        id={`${rule.ruleId}-weight`}
        type="number"
        min={0}
        value={String(rule.weight)}
        aria-label={`Waga reguły ${rule.title}`}
        className={cn("w-20 tabular-nums", decisive && "font-medium text-critical")}
        onChange={(event) => {
          const parsed = Number(event.target.value)
          onChange({ ...rule, weight: Number.isFinite(parsed) ? Math.max(parsed, 0) : 0 })
        }}
      />
      <Slider
        value={[Math.min(rule.weight, threshold)]}
        max={threshold}
        step={1}
        atRisk={decisive}
        aria-label={`Suwak wagi reguły ${rule.title}`}
        onValueChange={([value]) => onChange({ ...rule, weight: value ?? 0 })}
      />
      <span className="shrink-0 text-[12.5px] tabular-nums text-muted-foreground">
        z {threshold}
      </span>
    </div>
  )
}
